import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

function blankForm(sortOrder = 0) {
  return {
    id: null,
    title: '',
    slug: '',
    synopsis: '',
    audience: 'both',
    sort_order: sortOrder,
    is_active: true,
    published_at: '',
    cover_file: null,
    cover_name: '',
    cover_url: null,
    comic_category_id: '',
    comic_chapter_id: '',
  };
}

function blankCategory() {
  return { id: null, name: '', slug: '', description: '', sort_order: 0, is_active: true };
}

function blankChapter() {
  return { id: null, title: '', chapter_number: 1, description: '', sort_order: 0, is_active: true };
}

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function inferLinkType(url) {
  const u = String(url || '').toLowerCase();
  if (/\.wav(\?|$)/.test(u)) return 'audio';
  return 'video';
}

function createKasirComics() {
  return {
    comics: [],
    categories: [],
    chapters: [],
    loading: false,
    saving: false,
    savingTaxonomy: false,
    modal: false,
    editing: false,
    form: blankForm(),
    materiType: 'image',
    materiImageFiles: [],
    materiLinkUrl: '',
    materiAlt: '',
    pagesModal: false,
    activeComic: null,
    pageFiles: [],
    pageAlt: '',
    pageMateriType: 'image',
    pageLinkUrl: '',
    uploadingPages: false,
    categoryModal: false,
    categoryForm: blankCategory(),
    chapterModal: false,
    chapterForm: blankChapter(),

    init() {
      try {
        const cfg = JSON.parse(this.$el.getAttribute('data-config') || '{}');
        this.comics = cfg.comics || [];
        this.categories = cfg.categories || [];
        this.chapters = cfg.chapters || [];
      } catch (e) {
        console.error('kasirComics config', e);
      }
    },

    audienceLabel(a) {
      if (a === 'customer') return 'Customer';
      if (a === 'creator') return 'Creator';
      return 'Customer + Creator';
    },

    async reload() {
      this.loading = true;
      try {
        const [listRes, metaRes] = await Promise.all([
          axios.get('/api/v1/kasir/engagement/comics', { params: { per_page: 50 } }),
          axios.get('/api/v1/kasir/engagement/comics/meta'),
        ]);
        this.comics = listRes.data.data || [];
        this.categories = metaRes.data.data?.categories || [];
        this.chapters = metaRes.data.data?.chapters || [];
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal memuat comic.', 'error');
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.editing = false;
      this.form = blankForm((this.comics.length || 0) + 1);
      this.resetMateriDraft();
      this.modal = true;
    },

    openEdit(c) {
      this.editing = true;
      this.form = {
        id: c.id,
        title: c.title || '',
        slug: c.slug || '',
        synopsis: c.synopsis || '',
        audience: c.audience || 'both',
        sort_order: c.sort_order ?? 0,
        is_active: !!c.is_active,
        published_at: toLocalInput(c.published_at),
        cover_file: null,
        cover_name: '',
        cover_url: c.cover_url || null,
        comic_category_id: c.comic_category_id || c.category?.id || '',
        comic_chapter_id: c.comic_chapter_id || c.chapter?.id || '',
      };
      this.resetMateriDraft();
      this.modal = true;
    },

    resetMateriDraft() {
      this.materiType = 'image';
      this.materiImageFiles = [];
      this.materiLinkUrl = '';
      this.materiAlt = '';
    },

    closeModal() {
      this.modal = false;
      this.saving = false;
    },

    onCoverPicked(e) {
      this.form.cover_file = e.target.files?.[0] || null;
      this.form.cover_name = this.form.cover_file?.name || '';
    },

    onMateriImagesPicked(e) {
      this.materiImageFiles = Array.from(e.target.files || []);
    },

    buildFormData() {
      const fd = new FormData();
      fd.append('title', this.form.title || '');
      if (this.form.slug) fd.append('slug', this.form.slug);
      fd.append('synopsis', this.form.synopsis || '');
      fd.append('audience', this.form.audience || 'both');
      fd.append('sort_order', String(this.form.sort_order ?? 0));
      fd.append('is_active', this.form.is_active ? '1' : '0');
      if (this.form.published_at) {
        fd.append('published_at', this.form.published_at.replace('T', ' ') + ':00');
      }
      if (this.form.comic_category_id) {
        fd.append('comic_category_id', this.form.comic_category_id);
      } else {
        fd.append('comic_category_id', '');
      }
      if (this.form.comic_chapter_id) {
        fd.append('comic_chapter_id', this.form.comic_chapter_id);
      } else {
        fd.append('comic_chapter_id', '');
      }
      if (this.form.cover_file) fd.append('cover', this.form.cover_file);
      return fd;
    },

    hasPendingMateri() {
      if (this.materiType === 'image') return this.materiImageFiles.length > 0;
      return Boolean(String(this.materiLinkUrl || '').trim());
    },

    async uploadMateriForComic(comicId, replace = false) {
      if (!comicId || !this.hasPendingMateri()) return;
      const fd = new FormData();
      if (this.materiType === 'image') {
        fd.append('media_type', 'image');
        this.materiImageFiles.forEach((f) => fd.append('pages[]', f));
      } else {
        const url = String(this.materiLinkUrl || '').trim();
        fd.append('media_type', inferLinkType(url));
        fd.append('media_url', url);
      }
      if (this.materiAlt) fd.append('alt_text', this.materiAlt);
      if (replace) fd.append('replace', '1');
      await axios.post(`/api/v1/kasir/engagement/comics/${comicId}/materi`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    async submitForm() {
      if (this.saving) return;
      if (!this.form.title?.trim()) {
        window.toast?.('Judul wajib diisi.', 'error');
        return;
      }
      this.saving = true;
      try {
        const fd = this.buildFormData();
        let res;
        if (this.editing && this.form.id) {
          res = await axios.post(`/api/v1/kasir/engagement/comics/${this.form.id}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          res = await axios.post('/api/v1/kasir/engagement/comics', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        const comicId = res.data?.data?.id || this.form.id;
        if (comicId && this.hasPendingMateri()) {
          // Edit: timpa materi lama (ID sama). Create: tambah baru.
          await this.uploadMateriForComic(comicId, !!this.editing);
        }
        window.toast?.(res.data.message || 'Comic disimpan.', 'success');
        this.closeModal();
        await this.reload();
      } catch (e) {
        const payload = e?.response?.data;
        const first =
          payload?.errors && typeof payload.errors === 'object'
            ? Object.values(payload.errors).flat()?.[0]
            : null;
        window.toast?.(first || payload?.message || 'Gagal menyimpan comic.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async remove(c) {
      if (!window.confirm(`Hapus comic "${c.title}" beserta semua materi?`)) return;
      try {
        await axios.delete(`/api/v1/kasir/engagement/comics/${c.id}`);
        window.toast?.('Comic dihapus.', 'success');
        await this.reload();
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },

    async openPages(c) {
      this.pagesModal = true;
      this.pageFiles = [];
      this.pageAlt = '';
      this.pageMateriType = 'image';
      this.pageLinkUrl = '';
      try {
        const { data } = await axios.get(`/api/v1/kasir/engagement/comics/${c.id}`);
        this.activeComic = data.data;
      } catch (e) {
        this.activeComic = { ...c, pages: [] };
        window.toast?.(e?.response?.data?.message || 'Gagal memuat materi.', 'error');
      }
    },

    closePages() {
      this.pagesModal = false;
      this.activeComic = null;
      this.pageFiles = [];
    },

    onPagesPicked(e) {
      this.pageFiles = Array.from(e.target.files || []);
    },

    async uploadPages() {
      if (!this.activeComic?.id || this.uploadingPages) return;
      if (this.pageMateriType === 'image' && !this.pageFiles.length) {
        window.toast?.('Pilih file gambar terlebih dahulu.', 'error');
        return;
      }
      if (this.pageMateriType === 'link' && !String(this.pageLinkUrl || '').trim()) {
        window.toast?.('Masukkan link MP4/WAV.', 'error');
        return;
      }
      this.uploadingPages = true;
      try {
        const fd = new FormData();
        if (this.pageMateriType === 'image') {
          fd.append('media_type', 'image');
          this.pageFiles.forEach((f) => fd.append('pages[]', f));
        } else {
          const url = String(this.pageLinkUrl || '').trim();
          fd.append('media_type', inferLinkType(url));
          fd.append('media_url', url);
        }
        if (this.pageAlt) fd.append('alt_text', this.pageAlt);
        const { data } = await axios.post(
          `/api/v1/kasir/engagement/comics/${this.activeComic.id}/materi`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        this.activeComic = data.data;
        this.pageFiles = [];
        this.pageLinkUrl = '';
        this.pageAlt = '';
        window.toast?.(data.message || 'Materi ditambahkan.', 'success');
        await this.reload();
      } catch (e) {
        const payload = e?.response?.data;
        window.toast?.(payload?.message || 'Gagal upload materi.', 'error');
      } finally {
        this.uploadingPages = false;
      }
    },

    async removePage(p) {
      if (!this.activeComic?.id || !p?.id) return;
      if (!window.confirm('Hapus materi ini?')) return;
      try {
        const { data } = await axios.delete(
          `/api/v1/kasir/engagement/comics/${this.activeComic.id}/pages/${p.id}`,
        );
        this.activeComic = data.data;
        window.toast?.('Materi dihapus.', 'success');
        await this.reload();
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menghapus materi.', 'error');
      }
    },

    openCategoryForm(cat = null) {
      this.categoryForm = cat
        ? {
            id: cat.id,
            name: cat.name || '',
            slug: cat.slug || '',
            description: cat.description || '',
            sort_order: cat.sort_order ?? 0,
            is_active: !!cat.is_active,
          }
        : blankCategory();
      this.categoryModal = true;
    },

    async saveCategory() {
      if (this.savingTaxonomy) return;
      if (!this.categoryForm.name?.trim()) {
        window.toast?.('Nama kategori wajib.', 'error');
        return;
      }
      this.savingTaxonomy = true;
      try {
        const payload = {
          name: this.categoryForm.name,
          slug: this.categoryForm.slug || null,
          description: this.categoryForm.description || null,
          sort_order: this.categoryForm.sort_order ?? 0,
          is_active: this.categoryForm.is_active ? 1 : 0,
        };
        if (this.categoryForm.id) {
          await axios.put(`/api/v1/kasir/engagement/comics-categories/${this.categoryForm.id}`, payload);
        } else {
          await axios.post('/api/v1/kasir/engagement/comics-categories', payload);
        }
        this.categoryModal = false;
        window.toast?.('Kategori disimpan.', 'success');
        await this.reload();
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menyimpan kategori.', 'error');
      } finally {
        this.savingTaxonomy = false;
      }
    },

    async removeCategory(cat) {
      if (!window.confirm(`Hapus kategori "${cat.name}"?`)) return;
      try {
        await axios.delete(`/api/v1/kasir/engagement/comics-categories/${cat.id}`);
        window.toast?.('Kategori dihapus.', 'success');
        await this.reload();
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menghapus kategori.', 'error');
      }
    },

    openChapterForm(ch = null) {
      this.chapterForm = ch
        ? {
            id: ch.id,
            title: ch.title || '',
            chapter_number: ch.chapter_number ?? 1,
            description: ch.description || '',
            sort_order: ch.sort_order ?? 0,
            is_active: !!ch.is_active,
          }
        : blankChapter();
      this.chapterModal = true;
    },

    async saveChapter() {
      if (this.savingTaxonomy) return;
      if (!this.chapterForm.title?.trim()) {
        window.toast?.('Judul chapter wajib.', 'error');
        return;
      }
      this.savingTaxonomy = true;
      try {
        const payload = {
          title: this.chapterForm.title,
          chapter_number: this.chapterForm.chapter_number || 1,
          description: this.chapterForm.description || null,
          sort_order: this.chapterForm.sort_order ?? 0,
          is_active: this.chapterForm.is_active ? 1 : 0,
        };
        if (this.chapterForm.id) {
          await axios.put(`/api/v1/kasir/engagement/comics-chapters/${this.chapterForm.id}`, payload);
        } else {
          await axios.post('/api/v1/kasir/engagement/comics-chapters', payload);
        }
        this.chapterModal = false;
        window.toast?.('Chapter disimpan.', 'success');
        await this.reload();
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menyimpan chapter.', 'error');
      } finally {
        this.savingTaxonomy = false;
      }
    },

    async removeChapter(ch) {
      if (!window.confirm(`Hapus chapter "${ch.title}"?`)) return;
      try {
        await axios.delete(`/api/v1/kasir/engagement/comics-chapters/${ch.id}`);
        window.toast?.('Chapter dihapus.', 'success');
        await this.reload();
      } catch (e) {
        window.toast?.(e?.response?.data?.message || 'Gagal menghapus chapter.', 'error');
      }
    },
  };
}

Alpine.data('kasirComics', createKasirComics);
